import { useState, useEffect, lazy, Suspense } from 'react';
import { Button } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LazyComponentErrorBoundary from '../layout/LazyComponentErrorBoundary';
import ModalSkeleton from '../ui/skeletons/ModalSkeleton';
import ReactGA from 'react-ga4';
import { useStyles } from '../../styles/styles';

// Lazy load RSVPModal
const RSVPModal = lazy(() => import('../modals/RSVPModal'));

const RSVPButton = ({ 
  eventId, 
  eventName, 
  datetime, 
  location, 
  disabled = false, 
  variant = 'contained',
  color = 'secondary',
  onRSVPSuccess
}) => {
  const [hasRSVPed, setHasRSVPed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Check localStorage for existing RSVP status on component mount
  useEffect(() => {
    if (eventId) {
      const rsvpStatus = localStorage.getItem(`rsvp_status_${eventId}`);
      setHasRSVPed(rsvpStatus === 'true');
    }
  }, [eventId]);

  // Handle opening the modal
  const handleOpenModal = () => {
    if (!hasRSVPed && !disabled) {
      console.log('Opening RSVP modal with data:', {
        eventId,
        eventName,
        datetime,
        location
      });
      
      setModalOpen(true);
      
      // Track RSVP button click
      try {
        ReactGA.event({
          action: 'button_click',
          category: 'engagement',
          label: 'rsvp_button',
          custom_parameters: {
            event_id: eventId,
            event_name: eventName
          }
        });
      } catch (trackingError) {
        console.warn('Analytics tracking failed:', trackingError);
      }
    }
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Handle successful RSVP submission
  const handleRSVPSuccess = () => {
    if (eventId) {
      // Update localStorage to track RSVP status
      localStorage.setItem(`rsvp_status_${eventId}`, 'true');
      setHasRSVPed(true);
    }
    // Close the modal after successful submission
    setModalOpen(false);
    
    // Call the parent callback to refresh attendee data
    if (onRSVPSuccess) {
      onRSVPSuccess(eventId);
    }
  };

  // Determine button text based on RSVP status
  const getButtonContent = () => {
    if (hasRSVPed) {
      return "RSVP'd";
    }
    return "RSVP";
  };

  // Determine button styling based on RSVP status
  const getButtonProps = () => {
    if (hasRSVPed) {
      return {
        variant: 'contained',
        size: 'large',
        color: 'success',
        disabled: true,
        startIcon: <CheckCircleIcon />,
        sx: { 
          opacity: 0.8,
          cursor: 'default'
        }
      };
    }
    
    // Use MUI theme colors: primary = black, secondary = red
    return {
      variant: variant || 'contained',
      size: 'large',
      color: color || 'secondary',
      disabled,
      startIcon: <EventIcon />
    };
  };

  return (
    <>
      <Button
        onClick={handleOpenModal}
        {...getButtonProps()}
      >
        {getButtonContent()}
      </Button>

      {/* RSVPModal with Lazy Loading */}
      {modalOpen && (
        <LazyComponentErrorBoundary
          fallbackMessage="Unable to load RSVP form. Please try refreshing the page."
          minHeight="400px"
        >
          <Suspense 
            fallback={
              <ModalSkeleton 
                open={modalOpen} 
                onClose={handleCloseModal}
                variant="rsvp"
              />
            }
          >
            <RSVPModal
              open={modalOpen}
              handleClose={handleCloseModal}
              eventId={eventId}
              eventName={eventName}
              eventDateTime={datetime}
              eventLocation={location}
              onSuccess={handleRSVPSuccess}
            />
          </Suspense>
        </LazyComponentErrorBoundary>
      )}
    </>
  );
};

export default RSVPButton;